import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2,
  Plus,
  Calendar,
  Trash2,
  Image as ImageIcon,
  ArrowRight,
  ExternalLink,
  Ticket,
  RefreshCw,
} from "lucide-react";
import PortalShell from "@/components/PortalShell";
import { portalApi, portalMe } from "@/lib/portalApi";

const DAY_NAMES = { 0: "Mon", 1: "Tue", 2: "Wed", 3: "Thu", 4: "Fri", 5: "Sat", 6: "Sun" };
const NTH = { 1: "1st", 2: "2nd", 3: "3rd", 4: "4th", 5: "5th" };
const jsDayToOurDay = (js) => (js + 6) % 7;

const fmtDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d + "T00:00:00").toLocaleDateString("en-GB", {
      weekday: "short",
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return d;
  }
};

const recurrenceLabel = (ev) => {
  if (ev.recurrence === "monthly_nth" && ev.recurrence_nth) {
    const day = DAY_NAMES[jsDayToOurDay(new Date(ev.first_date + "T00:00:00").getDay())];
    return `Every ${NTH[ev.recurrence_nth]} ${day} of the month`;
  }
  if (ev.recurrence === "every_week") {
    const day = DAY_NAMES[jsDayToOurDay(new Date(ev.first_date + "T00:00:00").getDay())];
    return `Every ${day}`;
  }
  return "One-off";
};

export default function PortalEvents() {
  const [me, setMe] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [meRes, evRes] = await Promise.all([portalMe(), portalApi.get("/portal/events")]);
      setMe(meRes);
      setEvents(evRes.data);
    } catch {
      toast.error("Unable to load events.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const deleteEvent = async (id) => {
    if (!window.confirm("Delete this event? This can't be undone.")) return;
    try {
      await portalApi.delete(`/portal/events/${id}`);
      setEvents((es) => es.filter((e) => e.id !== id));
      toast.success("Event deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  // Split events by whether their first_date is in the past
  const todayIso = new Date().toISOString().slice(0, 10);
  const upcoming = events.filter((e) => (e.first_date || "") >= todayIso);
  const past = events.filter((e) => (e.first_date || "") < todayIso);

  return (
    <PortalShell me={me} testId="portal-events-page">
      <div className="max-w-[1400px] mx-auto px-6 md:px-8 py-10">
        {/* Header */}
        <div className="mb-10 flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.28em] text-[#D9A6F5] mb-3">Events</div>
            <h1 className="text-4xl md:text-5xl font-black tracking-tighter">
              {loading ? "Loading…" : `${events.length} event${events.length === 1 ? "" : "s"}`}
            </h1>
            <p className="mt-3 text-sm text-[#A3A3A3] max-w-xl">
              Everything you&apos;ve created. Click any event to edit its details, visibility, and
              per-occurrence themes.
            </p>
          </div>
          <Link
            to="/portal/events/new"
            data-testid="portal-events-create"
            className="inline-flex items-center gap-2 bg-[#D9A6F5] text-black hover:bg-[#E8C8FF] px-6 py-3 text-xs uppercase tracking-[0.22em] font-bold transition-colors"
          >
            <Plus size={14} /> Create new event
          </Link>
        </div>

        {loading ? (
          <div className="py-20 flex items-center justify-center">
            <Loader2 size={20} className="animate-spin text-[#D9A6F5]" />
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-24 border border-white/10 bg-[#0A0A0A]">
            <div className="inline-flex items-center justify-center h-16 w-16 border border-white/10 mb-6">
              <Calendar size={22} className="text-[#D9A6F5]" />
            </div>
            <p className="text-[#A3A3A3] mb-8">
              You haven&apos;t created any events yet. Let&apos;s get your first one live.
            </p>
            <Link
              to="/portal/events/new"
              className="inline-flex items-center gap-2 bg-[#D9A6F5] text-black hover:bg-[#E8C8FF] px-6 py-3 text-xs uppercase tracking-[0.22em] font-bold transition-colors"
            >
              <Plus size={14} /> Create your first event
            </Link>
          </div>
        ) : (
          <div className="space-y-12">
            <EventsGroup
              title="Upcoming"
              testId="events-upcoming"
              events={upcoming}
              me={me}
              deleteEvent={deleteEvent}
            />
            {past.length > 0 && (
              <EventsGroup
                title="Past"
                testId="events-past"
                events={past}
                me={me}
                deleteEvent={deleteEvent}
                dimmed
              />
            )}
          </div>
        )}
      </div>
    </PortalShell>
  );
}

function EventsGroup({ title, testId, events, me, deleteEvent, dimmed }) {
  if (events.length === 0) return null;
  return (
    <div data-testid={testId}>
      <div className="flex items-baseline justify-between mb-4">
        <h2 className="text-lg md:text-xl font-black tracking-tight">{title}</h2>
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#A3A3A3]">
          {events.length}
        </div>
      </div>
      <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 ${dimmed ? "opacity-70" : ""}`}>
        {events.map((ev) => (
          <div
            key={ev.id}
            data-testid={`portal-events-card-${ev.id}`}
            className="group bg-[#0A0A0A] border border-white/10 hover:border-[#D9A6F5]/40 overflow-hidden transition-colors"
          >
            <Link to={`/portal/events/${ev.id}`} className="block">
              <div className="aspect-[16/10] bg-black border-b border-white/10 flex items-center justify-center overflow-hidden">
                {ev.photo_data_uri ? (
                  <img src={ev.photo_data_uri} alt={ev.name} className="w-full h-full object-cover" />
                ) : (
                  <ImageIcon size={26} className="text-[#A3A3A3]/40" />
                )}
              </div>
              <div className="p-5">
                <h3 className="text-lg font-bold tracking-tight text-[#F5F5F0] truncate">
                  {ev.name}
                </h3>
                {ev.theme && (
                  <div className="mt-1 text-xs uppercase tracking-[0.2em] text-[#D9A6F5]">
                    {ev.theme}
                  </div>
                )}
                <div className="mt-3 flex items-center gap-1.5 text-sm text-[#A3A3A3]">
                  <Calendar size={12} className="text-[#D9A6F5]" />
                  {fmtDate(ev.first_date)} · {ev.time}
                </div>
                <div className="mt-1 flex items-center justify-between text-xs">
                  <span className="inline-flex items-center gap-1 text-[#A3A3A3]/80">
                    {ev.recurrence !== "none" && <RefreshCw size={10} />}
                    {recurrenceLabel(ev)}
                  </span>
                  {typeof ev.tickets_sold === "number" && ev.tickets_sold > 0 && (
                    <span className="inline-flex items-center gap-1 text-[#D9A6F5]">
                      <Ticket size={11} /> {ev.tickets_sold} sold
                    </span>
                  )}
                </div>
              </div>
            </Link>
            <div className="flex items-center justify-between px-5 py-3 border-t border-white/10 text-xs">
              {me?.venue?.slug ? (
                <a
                  href={`/v/${me.venue.slug}/${ev.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-[#A3A3A3] hover:text-[#D9A6F5] uppercase tracking-[0.2em]"
                >
                  Public page <ExternalLink size={11} />
                </a>
              ) : (
                <span />
              )}
              <div className="flex items-center gap-3">
                <Link
                  to={`/portal/guestlist?event_id=${ev.id}`}
                  className="inline-flex items-center gap-1 text-[#A3A3A3] hover:text-[#D9A6F5] uppercase tracking-[0.2em]"
                >
                  Guestlist
                </Link>
                <Link
                  to={`/portal/events/${ev.id}`}
                  data-testid={`portal-events-edit-${ev.id}`}
                  className="inline-flex items-center gap-1 text-[#F5F5F0] hover:text-[#D9A6F5] uppercase tracking-[0.2em]"
                >
                  Edit <ArrowRight size={11} />
                </Link>
                <button
                  type="button"
                  onClick={() => deleteEvent(ev.id)}
                  data-testid={`portal-events-delete-${ev.id}`}
                  className="text-[#A3A3A3] hover:text-red-400 p-1"
                  aria-label="Delete event"
                >
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
